<template>
  <CustomModal
    :is-modal-open="isOpen"
    @toggleModal="onCloseClick"
    @onClickAway="onCloseClick"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[768px] tw:md:w-[768px] tw:md:rounded-md tw:rounded-none tw:flex"
  >
    <template #modalHeader
      ><p class="tw:font-bold tw:text-xl tw:text-gray-800">{{ $t(getHeaderLabel) }}</p></template
    >

    <div v-if="currentStep === 1" class="tw:w-full">
      <SetupSelectCampaignType
        isChangingCampaign
        :selectedCampaignType="newSelectedCampaignType"
        :eligibleTypeOfCPACampaign="eligibleTypeOfCPACampaign"
        @onSelectedCampaignTypeChange="onSelectedCampaignTypeChange"
        :currentlyActiveCampaignType="currentCampaignType"
      />
    </div>
    <div v-else-if="currentStep === 2 && isSelectedCampaignTypeCPA" class="tw:w-full">
      <CPACampaignSetup
        isChangingCampaign
        :eligibleCommissionLevels="selectableCommissionLevels"
        :selectedCampaignType="selectedCampaignType"
        :selectedCommissionLevel="selectedCommissionLevel"
        @onSelectedCommissionChange="onSelectedCommissionChange"
      />
    </div>
    <div v-else-if="currentStep === 3 && isSelectedCampaignTypeCPA">
      <ChangeCampaignCPAConfirmation isChangingCampaign :isNetCPA="selectedCampaignType.toLowerCase() === 'netcpa'" />
    </div>
    <template #modalFooter>
      <div class="tw:hidden tw:md:flex tw:items-center tw:justify-between tw:w-full">
        <div class="tw:w-1/2">
          <p class="tw:text-xs">
            <b>{{ `Step ${currentStep}` }}</b
            >{{ ` out of 3` }}
          </p>
        </div>
        <div class="tw:flex tw:w-1/2 tw:justify-end tw:py-2 tw:gap-x-2">
          <GhostButton @click="onBackClick">{{
            currentStep === 1 ? $t('rc_campaign_type_change_cancel_button') : $t('tbs_modal_back')
          }}</GhostButton>

          <MainButton
            @click="onNextClick"
            :disabled="!selectedCampaignType || currentCampaignType === newSelectedCampaignType"
          >
            {{
              currentStep === 3
                ? $t('rc_campaign_type_change_save_button')
                : $t('rc_campaign_type_change_continue_button')
            }}
          </MainButton>
        </div>
      </div>
      <div class="tw:md:hidden tw:flex tw:flex-col tw:gap-2 tw:my-4 tw:w-full">
        <MainButton
          @click="onNextClick"
          :disabled="!selectedCampaignType || currentCampaignType === newSelectedCampaignType"
          >{{
            currentStep === 3
              ? $t('rc_campaign_type_change_save_button')
              : $t('rc_campaign_type_change_continue_button')
          }}</MainButton
        >
        <BaseButton @click="onBackClick" class="tw:text-gray-700 tw:font-bold">{{
          currentStep === 1 ? $t('rc_campaign_type_change_cancel_button') : $t('tbs_modal_back')
        }}</BaseButton>
      </div>
    </template>
    <div
      class="tw:fixed tw:h-full tw:bg-white tw:opacity-80 tw:z-50 tw:inset-0"
      v-show="isCampaignsFetching || isSubmittingChangedCampaign"
    >
      <LaunchpadLoader class="tw:h-full tw:flex tw:items-center tw:justify-center" />
    </div>
  </CustomModal>
</template>

<script setup>
import { ref, computed, onUpdated, onMounted } from 'vue'

import BaseButton from '@/components/base/buttons/BaseButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import SetupSelectCampaignType from '@/components/rateConnect/setup/SetupSelectCampaignType.vue'
import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import CPACampaignSetup from '@/components/rateConnect/setup/CPACampaignSetup.vue'
import ChangeCampaignCPAConfirmation from '@/components/rateConnect/settings/wizards/ChangeCampaignCPAConfirmation.vue'
import CustomModal from '@/components/base/CustomModal.vue'

import { useCampaign, useCampaignMutation, useCommissionLevels, usePartners } from '@/components/rateConnect/queries'
import { useChangeCampaign } from '@/components/rateConnect/settings/wizards/useChangeCampaign'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import { selectedAccommodationId } from '@/layouts/queries'

import appAnalytics from '@/utils/tracking'

defineProps({
  isOpen: Boolean,
})

const {
  currentStep,
  resetFlow,
  selectedCampaignType,
  selectedCommissionLevel,
  isSelectedCampaignTypeCPA,
  selectedNewPartner,
  source,
} = useChangeCampaign()
const { isFetching: isCampaignsFetching, currentCampaignType, currentPartner } = useCampaign(selectedAccommodationId)
const { isNetCpaEligible } = usePartners(selectedAccommodationId)
const { eligibleCommissionLevels } = useCommissionLevels()
const { updateCampaignType } = useCampaignMutation()
const { displayNotification, showSaveError } = useToastNotifications()

const emit = defineEmits(['onSubmitCampaignClick', 'onCloseClick'])

const changeCampaignModalRef = ref(null)
const isSubmittingChangedCampaign = ref(false)

const newSelectedCampaignType = computed(() => selectedCampaignType.value ?? currentCampaignType.value)

const eligibleTypeOfCPACampaign = computed(() => {
  if (selectedNewPartner.value) return selectedNewPartner.value?.canNetCpa ? 'netcpa' : currentCampaignType.value
  else return isNetCpaEligible.value ? 'netcpa' : currentCampaignType.value
})

const selectableCommissionLevels = computed(() => {
  return eligibleCommissionLevels.value
})

const onSelectedCampaignTypeChange = type => (selectedCampaignType.value = type)
const onSelectedCommissionChange = level => (selectedCommissionLevel.value = level)

onMounted(() => {
  onSelectedCampaignTypeChange('netcpa')
})

onUpdated(() => {
  if (!selectedCampaignType.value && currentCampaignType.value === 'cpc') {
    onSelectedCampaignTypeChange('netcpa')
  }
})

const getHeaderLabel = computed(() => {
  if (currentStep.value === 1) return 'rc_setup_campagn_header'
  else if (currentStep.value === 2) return 'rc_setup_commission_header'
  else if (currentStep.value === 3) return 'rc_campaign_type_changes_head'
  else return ''
})

const onNextClick = () => {
  if (currentStep.value === 1 && isSelectedCampaignTypeCPA.value) selectedCommissionLevel.value = 18
  if (currentStep.value < 3) currentStep.value += 1
  else if (currentStep.value === 3) {
    if (selectedNewPartner.value) {
      emit('onSubmitCampaignClick')
      changeCampaignModalRef.value?.close()
    } else {
      submitUpdatedCampaign()
    }
  }
}
const onBackClick = () => {
  if (currentStep.value === 1) onCloseClick()
  else currentStep.value -= 1
}

const submitUpdatedCampaign = async () => {
  isSubmittingChangedCampaign.value = true
  let body = { commissionLevel: selectedCommissionLevel.value, partnerId: currentPartner.value }

  await updateCampaignType(
    {
      accommodationId: selectedAccommodationId.value,
      body,
    },
    {
      onSuccess: () => {
        isSubmittingChangedCampaign.value = false
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_change_campaign_type_pay_per_stay_success_message',
          isTranslationKey: true,
          isHtml: true,
          type: 'success',
        })
        onCloseClick()
        if (source === 'pauseFlow') {
          appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_TYPE_CHANGE_SUCCESS, {
            accommodationId: selectedAccommodationId.value,
            campaignType: currentCampaignType.value,
            isNetCPAEligible: isNetCpaEligible.value,
          })
        }
      },
      onError: () => {
        isSubmittingChangedCampaign.value = false
        showSaveError()
        onCloseClick()
      },
    }
  )
}

const showModal = () => {
  changeCampaignModalRef.value?.showModal()
}

const onCloseClick = () => {
  resetFlow()
  isSubmittingChangedCampaign.value = false
  emit('onCloseClick')
}

defineExpose({
  showModal,
})
</script>
